import { useState, useEffect, useCallback } from 'react';
import { EnergyKpiStrip } from './EnergyKpiStrip';
// SolarBatterySummary replaced with EnergyMixCompact + Air Quality panels (4-quadrant layout)
import { ProductionLeaderboard } from './GroupLeaderboard';
import { OffGridTrendChart } from './OffGridTrendChart';
import { EnergyMixChart } from './EnergyMixChart';
import { EnergyMixCompact } from './EnergyMixCompact';
import { AirQualityGauge, CarbonOffsetMini } from './AirQualityGrid';
import { EnergyAlertStrip } from './EnergyAlertStrip';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import { UnitModelFilter } from '@/components/layout/UnitModelFilter';
import { IndustryFilter } from '@/components/layout/IndustryFilter';
import { authenticatedFetch } from '@/lib/api';
import { useFilters } from '@/contexts/FilterContext';
import { useLabels } from '@/hooks/useLabels';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useLiveDemoContext } from '@/contexts/LiveDemoContext';
import { useLiveDemo } from '@/hooks/useLiveDemo';
import type { EnergyDashboardData, EnergyFilters } from '@/types/energy';
import { buildEnergyDashboardParams } from '@/lib/energyDashboardParams';

interface EnergyDashboardProps {
  dateRange: EnergyFilters['dateRange'];
  onLastUpdatedChange?: (date: Date | null) => void;
}

/**
 * Renders the Energy Dashboard UI that shows KPIs, summaries, charts, and filters for the selected date range and filter set.
 *
 * Fetches dashboard data according to current filters and date range, supports retry on error, and integrates a live-demo mode to animate real-time-like updates.
 *
 * @param dateRange - Selected date range identifier (e.g., "30d"); controls the scope of displayed data.
 * @returns The energy dashboard component tree containing header, KPI strip, summaries, charts, filter controls, and alert strip.
 */
export function EnergyDashboard({
  dateRange,
  onLastUpdatedChange,
}: EnergyDashboardProps) {
  const isMobile = useIsMobile();
  const [data, setData] = useState<EnergyDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live demo mode - simulates real-time data updates
  const { isLiveDemoEnabled } = useLiveDemoContext();
  const { data: displayData, isLive } = useLiveDemo({
    enabled: isLiveDemoEnabled,
    dateRange,
    baseData: data,
  });

  // Notify parent of last updated time changes
  useEffect(() => {
    if (onLastUpdatedChange) {
      const lastUpdatedStr = displayData?.lastUpdated || data?.lastUpdated;
      onLastUpdatedChange(lastUpdatedStr ? new Date(lastUpdatedStr) : null);
    }
  }, [displayData?.lastUpdated, data?.lastUpdated, onLastUpdatedChange]);

  // Get filter state from FilterContext
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedIndustryTypes,
    selectedModels,
    companies,
    groups,
  } = useFilters();

  // Get dynamic labels
  const { getLabel } = useLabels();
  const groupLabel = getLabel('group');

  // Check if filters result in zero units (empty state)
  // Any filter being empty means no units can match
  const hasEmptyFilters =
    selectedModels.length === 0 ||
    selectedGroupIds.length === 0 ||
    selectedCompanyIds.length === 0 ||
    selectedIndustryTypes.length === 0;

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Build filter params (demo mode is detected by hostname, not URL params)
      const filterParams = buildEnergyDashboardParams({
        dateRange,
        groups,
        selectedCompanyIds,
        companyCount: companies.length,
        selectedGroupIds,
        selectedIndustryTypes,
        selectedModels,
        today: new Date().toISOString().split('T')[0],
      });

      const queryString = filterParams.toString();
      const response = await authenticatedFetch(`/api/v1/energy/dashboard?${queryString}`);

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const dashboardData = await response.json();
      setData(dashboardData);
      setError(null);
    } catch (err) {
      console.error('Energy dashboard error:', err);
      setError('Failed to load energy dashboard');
    } finally {
      setLoading(false);
    }
  }, [dateRange, groups, selectedCompanyIds, selectedGroupIds, selectedIndustryTypes, selectedModels, companies.length]);

  // Refetch when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading && !data) {
    // Layout-aware skeleton that matches the actual dashboard structure
    // to prevent layout shift when data loads
    return (
      <div className="flex flex-col h-full overflow-auto">
        {/* Mobile Filter Bar skeleton */}
        {isMobile && <div className="h-12 bg-card border-b border-border shrink-0" />}

        <div className={`flex flex-col gap-4 ${isMobile ? 'pl-4 pr-5 pt-4 flex-1' : 'p-6'}`}>
          {/* Desktop Header skeleton */}
          <div className="hidden sm:flex sm:items-center sm:justify-between">
            <div className="h-8 w-64 bg-muted/50 rounded animate-pulse" />
            <div className="h-4 w-32 bg-muted/50 rounded animate-pulse" />
          </div>

          {/* KPI Strip skeleton - 5 cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-card border border-border rounded-lg animate-pulse" />
            ))}
          </div>

          {/* Middle section skeleton - 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-64 bg-card border border-border rounded-lg animate-pulse" />
            <div className="h-64 bg-card border border-border rounded-lg animate-pulse" />
          </div>

          {/* Bottom section skeleton - 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-72 bg-card border border-border rounded-lg animate-pulse" />
            <div className="h-72 bg-card border border-border rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-destructive">
          <p>{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-[#8FB560] text-black rounded hover:bg-[#7a9d50] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Empty state when filters result in zero units
  if (hasEmptyFilters) {
    return (
      <div className="flex flex-col h-full overflow-auto relative">
        {/* Mobile Filter Bar - date range + reset live in the mobile menu */}
        {isMobile && (
          <div className="h-12 bg-card border-b border-border flex items-center px-3 gap-2 shrink-0">
            <div className="flex items-center gap-1.5">
              <IndustryFilter />
              <CompanyGroupFilters />
              <UnitModelFilter highlight={selectedModels.length === 0} />
            </div>
          </div>
        )}

        <div className={`flex flex-col gap-4 ${isMobile ? 'pl-4 pr-5 pt-4 flex-1' : 'p-6'} relative`}>
          {/* KPI Strip skeleton - 5 cards (dimmed) */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 opacity-40">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-card border border-border rounded-lg" />
            ))}
          </div>

          {/* Middle section skeleton - 2 columns (dimmed) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 opacity-40">
            <div className="h-64 bg-card border border-border rounded-lg" />
            <div className="h-64 bg-card border border-border rounded-lg" />
          </div>

          {/* Bottom section skeleton - 2 columns (dimmed) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 opacity-40">
            <div className="h-72 bg-card border border-border rounded-lg" />
            <div className="h-72 bg-card border border-border rounded-lg" />
          </div>

          {/* Empty state modal overlay - centered over dimmed skeleton */}
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-4 shadow-xl">
              <div className="text-center space-y-4">
                {/* Icon/Glyph - matching Reports page style (h-12 w-12) */}
                {selectedModels.length === 0 ? (
                  // Truck icon when models filter is empty
                  <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                    <svg className="h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                    </svg>
                  </div>
                ) : (
                  // Filter icon for other empty filter cases
                  <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                    <svg className="h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                    </svg>
                  </div>
                )}

                {/* Title and subtitle - matching Reports page text sizes */}
                <div>
                  <p className="text-lg text-muted-foreground font-medium mb-1">
                    No Matches with Current Filters
                  </p>
                  <p className="text-sm text-muted-foreground/80">
                    {selectedCompanyIds.length === 0 && selectedGroupIds.length === 0 && selectedModels.length === 0 && selectedIndustryTypes.length === 0
                      ? 'Use filters above to select units'
                      : <>
                          {selectedGroupIds.length === 0 && selectedModels.length === 0 ? (
                            <><span className="text-[#8FB560] font-medium">{groupLabel}</span> and <span className="text-[#8FB560] font-medium">Model</span> needed</>
                          ) : selectedGroupIds.length === 0 ? (
                            <><span className="text-[#8FB560] font-medium">{groupLabel}</span> needed</>
                          ) : (
                            <><span className="text-[#8FB560] font-medium">Model</span> needed</>
                          )}
                        </>
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Mobile Filter Bar - date range + reset live in the mobile menu; desktop uses EnergyStatusBar in parent Dashboard.tsx */}
      {isMobile && (
        <div className="h-12 bg-card border-b border-border flex items-center px-3 gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            <IndustryFilter />
            <CompanyGroupFilters />
            <UnitModelFilter highlight={selectedModels.length === 0} />
          </div>
        </div>
      )}

      {/* Content Area - with padding, scrollable, safe area for iOS on mobile */}
      {/* Mobile: extra right padding (pr-5) to prevent scrollbar from overlapping cards */}
      <div className={`flex flex-col gap-4 ${isMobile ? 'pl-4 pr-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex-1' : 'p-6'}`}>
        {/* Top KPI Strip */}
        <EnergyKpiStrip
          kpis={displayData?.kpis || data.kpis}
          dateRange={dateRange}
          days={data.days}
          animateValues={isLive}
        />

        {/* Middle Section: 4-quadrant layout + Production Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: 4-quadrant grid (Energy Mix + Trees + CO₂ + VOC + PM2.5) */}
          <div className="grid grid-cols-4 grid-rows-2 gap-2 min-h-[280px]">
            {/* Top-left: Energy Mix (2 cols) */}
            <EnergyMixCompact
              data={displayData?.energyMixSummary ?? data.energyMixSummary ?? null}
              solarData={displayData?.solarBattery || data.solarBattery}
              animateValues={isLive}
              className="col-span-2"
            />

            {/* Top-right: Trees (1 col) */}
            <CarbonOffsetMini
              co2MetricTons={displayData?.kpis.totalCO2Reduced ?? data.kpis.totalCO2Reduced ?? null}
              isLive={isLive}
            />

            {/* Top-right: CO₂ (1 col). airQuality is null on prod (no
                Atmocube hardware) — the gauges render their N/A state. */}
            <AirQualityGauge
              data={displayData?.airQuality?.co2 ?? data.airQuality?.co2 ?? null}
              type="co2"
              size="lg"
              isLive={isLive}
            />

            {/* Bottom-left: VOC (2 cols, under Energy Mix) */}
            <AirQualityGauge
              data={displayData?.airQuality?.vocIndex ?? data.airQuality?.vocIndex ?? null}
              type="voc"
              size="wide"
              isLive={isLive}
              className="col-span-2"
            />

            {/* Bottom-right: PM2.5 (2 cols, under Trees+CO₂) */}
            <AirQualityGauge
              data={displayData?.airQuality?.pm25 ?? data.airQuality?.pm25 ?? null}
              type="pm25"
              size="wide"
              isLive={isLive}
              className="col-span-2"
            />
          </div>
          <ProductionLeaderboard data={data.leaderboard} unitData={data.unitLeaderboard} />
        </div>

        {/* Bottom Section: Trend Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OffGridTrendChart data={data.offGridTrend} />
          <EnergyMixChart data={data.energyMix} />
        </div>

        {/* Alert Strip */}
        {data.alerts.length > 0 && (
          <EnergyAlertStrip alerts={data.alerts} />
        )}
      </div>
    </div>
  );
}